import React, { useCallback, useMemo, useState } from 'react';
import { useWindowDimensions } from 'react-native';
import { useMutation } from '@apollo/client';
import moment from 'moment';
import Toast from 'react-native-root-toast';
import HtmlRenderer from 'react-native-render-html';

import { ACKNOWLEDGE_MY_WARNING } from '../../graphql/people';
import { Text } from '../../components';
import { ActionDock, Card, Screen, SectionTitle } from '../../design/components';
import { useTheme } from '../../theme';
import { useThemedStyles } from '../../hooks/useThemedStyles';
import { getHtmlBoldStyle } from '../../utils/htmlBoldStyle';

const WARNING_HTML_IGNORED_STYLES = [
	'background',
	'background-color',
	'color',
];

const MyWarningDetailView = ({ route }) => {
	const styles = useThemedStyles(createStyles);
	const { width } = useWindowDimensions();
	const { theme: { ui } } = useTheme();
	const htmlTagStyles = useMemo(() => {
		const bold = getHtmlBoldStyle(ui);
		return {
			p: { color: ui.colors.text },
			li: { color: ui.colors.text },
			strong: bold,
			b: bold,
		};
	}, [ui]);
	const initialWarning = route?.params?.warning || null;

	const [acknowledgeWarning, acknowledgeStatus] = useMutation(ACKNOWLEDGE_MY_WARNING);
	// The mutation response refreshes the cached warning; keep a local copy so the screen
	// reflects the acknowledgement immediately.
	const [warningOverride, setWarningOverride] = useState(null);
	const warning = warningOverride || initialWarning;

	const issuedByName = useMemo(() => (
		[warning?.issuedBy?.firstname, warning?.issuedBy?.lastname].filter(Boolean).join(' ').trim()
	), [warning?.issuedBy?.firstname, warning?.issuedBy?.lastname]);

	const acknowledge = useCallback(async () => {
		if (!warning?.id) {
			return;
		}

		try {
			const response = await acknowledgeWarning({ variables: { id: warning.id } });
			const acknowledgedWarning = response?.data?.acknowledgeEmployeeWarning;
			if (acknowledgedWarning) {
				setWarningOverride(acknowledgedWarning);
			}
			Toast.show('Warning acknowledged');
		}
		catch (error) {
			console.error('Warning acknowledgement failed', error);
			Toast.show(error?.message || 'Unable to acknowledge warning');
		}
	}, [acknowledgeWarning, warning?.id]);

	if (!warning) {
		return null;
	}

	const contentHtml = String(warning.contentHtml || '').trim();
	const meta = [
		warning.typeLabel,
		[
			warning.issuedAt ? `Issued ${moment(warning.issuedAt).format('D MMM YYYY')}` : '',
			issuedByName ? `by ${issuedByName}` : '',
		].filter(Boolean).join(' '),
	].filter(Boolean).join(' · ');

	return (
		<Screen
			backLabel="Warnings"
			heading={warning.summary || warning.typeLabel || 'Warning'}
			subheading={meta}
			dock={!warning.acknowledged && (
				<ActionDock
					action={{
						label: 'Acknowledge warning',
						onPress: acknowledge,
						loading: acknowledgeStatus.loading,
					}}
				/>
			)}
		>
			{
				warning.acknowledged ? (
					<Card tone="success">
						<Text style={styles.acknowledgedText}>
							Acknowledged
							{warning.acknowledgedAt ? ` on ${moment(warning.acknowledgedAt).format('D MMM YYYY, HH:mm')}` : ''}
						</Text>
					</Card>
				) : (
					<Card tone="warning">
						<Text style={styles.attentionText}>
							This warning requires your acknowledgement.
						</Text>
					</Card>
				)
			}

			{
				warning.details ? (
					<Card style={styles.card}>
						<SectionTitle title="Details" />
						<Text style={styles.bodyText}>{warning.details}</Text>
					</Card>
				) : null
			}

			{
				contentHtml ? (
					<Card>
						<HtmlRenderer
							baseFontStyle={styles.htmlBase}
							imagesMaxWidth={Math.max(1, Math.min(width, ui.layout.maxContentWidth) - 70)}
							html={contentHtml}
							ignoredStyles={WARNING_HTML_IGNORED_STYLES}
							tagsStyles={htmlTagStyles}
						/>
					</Card>
				) : null
			}
		</Screen>
	);
};

const createStyles = ({ ui: { colors, type, space } }) => ({
	card: {
		gap: space.sm,
	},
	bodyText: {
		...type.bodySmall,
		lineHeight: 21,
		color: colors.text,
	},
	htmlBase: {
		...type.bodySmall,
		lineHeight: 21,
		color: colors.text,
	},
	acknowledgedText: {
		...type.bodySmallSemibold,
		color: colors.onSuccessCard,
	},
	attentionText: {
		...type.bodySmallSemibold,
		color: colors.warning,
	},
});

export default MyWarningDetailView;
